import { Badge, Banner, Button, Card } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import { friendlyError } from '../../shared/api/errors.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useOpenBillingPortal } from './hooks.js';
import { LegacyPlanPicker } from './LegacyPlans.js';
import { PLAN_CARDS, formatPlanPrice } from './plans.js';
import { isSubscribed } from '../../shared/lib/subscription.js';

/**
 * Settings › Billing (doc 23).
 *
 * Deliberately one button. Cards, invoices, VAT details, plan changes and
 * cancellation all live in Stripe's own portal, which is localised into every
 * market we sell in and already handles SCA, proration and dunning correctly.
 * Rebuilding any of that here would be several screens of work whose best
 * possible outcome is parity.
 */
export function BillingCard({ business, canceled, confirming }: { business: BusinessDoc; canceled?: boolean; confirming?: boolean }) {
  const { t, tag } = useI18n();
  const portal = useOpenBillingPortal(business.id);
  const { status, plan, currentPeriodEnd, cancelAtPeriodEnd } = business.subscription;

  const card = PLAN_CARDS.find((p) => p.tier === plan);
  const renews = currentPeriodEnd
    ? new Intl.DateTimeFormat(tag, { dateStyle: 'long' }).format(currentPeriodEnd)
    : null;

  return (
    <Card title={t('settings.billing.title')}>
      {/* A failing card is the one billing state with a deadline attached, so it
          gets a banner rather than a badge. Service is NOT cut while Stripe
          retries (see `isSubscribed`) — the message asks, it does not threaten. */}
      {status === 'past_due' && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="warning" title={t('settings.billing.pastDue.title')}>
            {t('settings.billing.pastDue.body')}
          </Banner>
        </div>
      )}

      <div className="billing-row">
        <span className="billing-row__label">{t('settings.billing.status')}</span>
        <span>
          <Badge tone={statusTone(status)}>{t(`settings.billing.status.${status}` as never)}</Badge>
        </span>
      </div>

      {card && (
        <div className="billing-row">
          <span className="billing-row__label">{t('settings.billing.plan')}</span>
          <span>
            {t(card.nameKey)} · {formatPlanPrice(card.amount[business.currency], business.currency, tag)}
            {t('billing.perMonth')}
          </span>
        </div>
      )}

      {renews && (
        <div className="billing-row">
          <span className="billing-row__label">
            {/* "Renews on" and "ends on" are the same date and opposite news.
                Reading one as the other is how a cancellation goes unnoticed
                until the phone stops being answered. */}
            {cancelAtPeriodEnd ? t('settings.billing.endsOn') : t('settings.billing.renewsOn')}
          </span>
          <span>{renews}</span>
        </div>
      )}

      {portal.isError && (
        <div style={{ marginTop: 16 }}>
          <Banner tone="danger">{friendlyError(t, portal.error)}</Banner>
        </div>
      )}

      {isSubscribed(business) ? (
        <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button loading={portal.isPending} onClick={() => portal.mutate()}>
            {t('settings.billing.manage')}
          </Button>
        </div>
      ) : (
        // Not subscribed (lapsed or cancelled): the plans, right here. While a
        // Checkout return is still confirming, the section shows that instead
        // of offering a second subscription.
        !confirming && (
          <div style={{ marginTop: 20 }}>
            <LegacyPlanPicker business={business} canceled={canceled} />
          </div>
        )
      )}
      <p className="field-hint" style={{ marginTop: 12 }}>{t('settings.billing.hint')}</p>
    </Card>
  );
}

function statusTone(status: BusinessDoc['subscription']['status']) {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success' as const;
    case 'past_due':
      return 'warning' as const;
    default:
      return 'neutral' as const;
  }
}
